import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { expeditionService } from "../services/expedition";
import { productService } from "../services/product";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"; // Importar FontAwesomeIcon
import { formatDateForDisplay } from "../utils/dateUtils";
import { getUnitWeight } from "../utils/packUtils";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import {
  faChevronDown,
  faChevronUp,
  faCheckCircle,
  faTimesCircle,
  faTimes,
} from "@fortawesome/free-solid-svg-icons"; // Ícones
import { toast } from "react-hot-toast"; // Para notificações

// Valores por omissão de todos os filtros (referência estável para "Limpar").
const DEFAULT_FILTERS = {
  status: "",
  date_type: "delivery_date",
  start_date: "",
  end_date: "",
  is_weekly: "",
  sort_order: "order_number_desc",
  order_number: "",
  client_name: "",
};

const Expediction = () => {
  const [expeditions, setExpeditions] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // `draftFilters` reflete o que está nos campos do formulário. `filters` é o
  // que foi efetivamente aplicado e usado na busca — só é atualizado após um
  // pequeno debounce (ou de imediato ao clicar em "Buscar"/"Limpar"), evitando
  // disparar uma busca a cada tecla digitada ou a cada troca de filtro.
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const debouncedFilters = useDebouncedValue(draftFilters, 600);
  const isFirstFilterRun = useRef(true);
  // Evita que uma resposta antiga (de uma busca anterior ainda em curso)
  // sobrescreva o resultado de uma busca mais recente — a causa do efeito de
  // "fica carregando, para, e volta a carregar".
  const fetchIdRef = useRef(0);

  const navigate = useNavigate();
  const [openExpeditionId, setOpenExpeditionId] = useState(""); // Estado para controlar qual collapse está aberto (usar string vazia para nenhum aberto)
  const [editableExpeditionData, setEditableExpeditionData] = useState({}); // Estado para dados editáveis
  const [pendingBoxTypeChanges, setPendingBoxTypeChanges] = useState({}); // Estado para mudanças pendentes de tipo de caixa
  const [pendingLotChanges, setPendingLotChanges] = useState({}); // Estado para mudanças pendentes de lote
  const [boxTypeOptions, setBoxTypeOptions] = useState([]); // Tipos de caixa disponíveis (produto DPD/OX + tipos criados manualmente)

  useEffect(() => {
    productService
      .getBoxTypes()
      .then(setBoxTypeOptions)
      .catch(() => setBoxTypeOptions([]));
  }, []);

  // Estados para funcionalidade de dividir expedição
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitItemId, setSplitItemId] = useState(null);
  const [splitQuantity, setSplitQuantity] = useState(0);
  const [splitError, setSplitError] = useState("");
  const [availableLots, setAvailableLots] = useState({});
  const [splitExpeditionId, setSplitExpeditionId] = useState(null);
  const itemsPerPage = 8;
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));

  // Track which product lots are currently loading to prevent infinite loops
  const loadingLotsRef = useRef(new Set());

  // ✅ Load all available lots for products in expeditions when expeditions change
  useEffect(() => {
    const loadAllAvailableLots = async () => {
      const lotsToLoad = new Map(); // expeditionId -> Set of productIds

      // Collect all unique product IDs across all expeditions
      expeditions.forEach(expedition => {
        if (!lotsToLoad.has(expedition.id)) {
          lotsToLoad.set(expedition.id, new Set());
        }

        expedition.expedition_items?.forEach(item => {
          // Get parent product ID if variant
          let targetProductId = item.product_id;
          if (item.products?.is_variant && item.products?.parent_product_id) {
            targetProductId = item.products.parent_product_id;
          }

          // Only load if not already loaded and not currently loading
          if (!availableLots[targetProductId] && !loadingLotsRef.current.has(targetProductId)) {
            lotsToLoad.get(expedition.id).add(targetProductId);
          }
        });
      });

      // Load lots for each product
      for (const [expeditionId, productIds] of lotsToLoad.entries()) {
        for (const productId of productIds) {
          loadingLotsRef.current.add(productId);

          try {
            const lots = await expeditionService.getAvailableLots(expeditionId, productId);
            setAvailableLots(prev => ({
              ...prev,
              [productId]: lots
            }));
          } catch (err) {
            console.error(`❌ Erro ao carregar lotes para produto ${productId}:`, err);
          } finally {
            loadingLotsRef.current.delete(productId);
          }
        }
      }
    };

    if (expeditions.length > 0) {
      loadAllAvailableLots();
    }
  }, [expeditions]);

  const statusMap = {
    pending: "Pendente",
    delivered: "Entregue",
    cancelled: "Cancelado",
    // Removidos: packed, overdue, processing, divided, confirmed, on_hold, returned, completed, shipped
  };

  const fetchExpeditions = async () => {
    const requestId = ++fetchIdRef.current;
    try {
      setLoading(true);
      setOpenExpeditionId(""); // Fecha os collapses apenas numa busca nova (mudança de filtros)
      // Paginação real no servidor (evita o corte silencioso de 1000 linhas do
      // Supabase quando nenhum filtro de status é aplicado).
      const { data, total } = await expeditionService.getExpeditions({
        ...filters,
        page: currentPage,
        page_size: itemsPerPage,
      });
      if (requestId !== fetchIdRef.current) return; // Resposta desatualizada — ignorar

      // Preencher automaticamente o tipo de caixa baseado no produto
      const expeditionsWithBoxType = (data || []).map(expedition => ({
        ...expedition,
        expedition_items: expedition.expedition_items.map(item => ({
          ...item,
          // Se box_type do item estiver vazio e o produto tiver box_type, usar o do produto
          box_type: item.box_type || item.products?.box_type || ''
        }))
      }));

      setExpeditions(Array.isArray(expeditionsWithBoxType) ? expeditionsWithBoxType : []);
      // `total` só vem preenchido na página 1 (o backend evita repetir o
      // COUNT(*) em toda troca de página); nas seguintes mantém o valor atual.
      if (total !== undefined) setTotalItems(total);
      setError("");
    } catch (err) {
      if (requestId !== fetchIdRef.current) return; // Resposta desatualizada — ignorar
      // Em caso de erro, definir expeditions como vazio (não mostrar erro)
      console.error("Erro ao buscar expedições:", err);
      setExpeditions([]);
      setTotalItems(0);
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpeditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage]);

  // Qualquer alteração num campo do formulário (texto, select ou data) só
  // atualiza o rascunho — a busca real só acontece após o debounce abaixo.
  const handleDraftChange = (e) => {
    const { name, value } = e.target;
    setDraftFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFiltersNow = (newFilters) => {
    setFilters(newFilters);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', '1');
      return newParams;
    });
  };

  // Aplica o rascunho de imediato, sem esperar o debounce.
  const handleSearch = () => {
    applyFiltersNow(draftFilters);
  };

  const handleClearFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    applyFiltersNow(DEFAULT_FILTERS);
  };

  // Aplica o rascunho automaticamente ~600ms depois do utilizador parar de
  // mexer nos filtros (digitar, escolher data, mudar select, etc.).
  useEffect(() => {
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }
    applyFiltersNow(debouncedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters]);

  // Paginação: `expeditions` já vem paginado do servidor (ver fetchExpeditions),
  // `totalItems` reflete o total real de linhas que correspondem aos filtros.
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + expeditions.length;
  const paginatedExpeditions = expeditions;

  const handlePageChange = (page) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) handlePageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) handlePageChange(currentPage + 1);
  };

  const handleToggleCollapse = (expeditionId) => {
    // Se o collapse clicado já estiver aberto, feche-o. Caso contrário, abra-o.
    const newOpenExpeditionId =
      openExpeditionId === expeditionId ? "" : expeditionId; // Usar string vazia para fechar
    setOpenExpeditionId(newOpenExpeditionId);

    // Se estamos abrindo um novo collapse, inicialize os dados editáveis
    if (newOpenExpeditionId === expeditionId) {
      const currentExpedition = expeditions.find(
        (exp) => exp.id === expeditionId
      );
      if (currentExpedition) {
        setEditableExpeditionData({
          ...currentExpedition,
          shipping_company: currentExpedition.shipping_company || "",
          invoice_number: currentExpedition.invoice_number || "",
          notes: currentExpedition.notes || "",
        });

        // ✅ Auto-selecionar lote com maior estoque para itens sem lote definido
        const autoLotChanges = {};
        currentExpedition.expedition_items?.forEach(item => {
          if (!item.production_lot_id) {
            // Determinar produto para buscar lotes (considerar variantes)
            let targetProductId = item.product_id;
            if (item.products?.is_variant && item.products?.parent_product_id) {
              targetProductId = item.products.parent_product_id;
            }
            // Se há lotes disponíveis, selecionar o primeiro (maior estoque)
            const lots = availableLots[targetProductId] || [];
            if (lots.length > 0) {
              autoLotChanges[item.id] = lots[0].id;
            }
          }
        });
        // Definir lotes auto-selecionados como mudanças pendentes — mesclar
        // com o estado existente (não substituir), para não perder seleções
        // manuais já feitas noutros itens/expedições ainda por guardar.
        if (Object.keys(autoLotChanges).length > 0) {
          setPendingLotChanges(prev => ({ ...autoLotChanges, ...prev }));
        }
      }
    } else {
      // Se estamos fechando o collapse, limpe os dados editáveis e mudanças pendentes
      setEditableExpeditionData({});
      setPendingBoxTypeChanges({});
      setPendingLotChanges({});
    }
  };

  const handleEditableChange = (e) => {
    const { name, value } = e.target;
    setEditableExpeditionData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveDetails = async (expeditionId) => {
    try {
      setLoading(true);
      const currentOpenExpeditionId = openExpeditionId; // Salvar o ID da expedição aberta

      // As mudanças de tipo de caixa e lote serão incluídas no updateExpedition abaixo
      // Não é necessário fazer chamadas individuais pois o updateExpedition envia todos os dados

      const dataToSave = {
        order_id: editableExpeditionData.order_id,
        shipping_company: editableExpeditionData.shipping_company,
        invoice_number: editableExpeditionData.invoice_number,
        status: editableExpeditionData.status,
        is_packed: editableExpeditionData.is_packed,
        notes: editableExpeditionData.notes,
        expedition_items: editableExpeditionData.expedition_items.map(
          (item) => ({
            // ✅ Identificar a linha exata: sem isto, o backend só consegue
            // localizar itens por product_id, e ao existirem vários itens do
            // mesmo produto (divisão de expedição) ele confunde/sobrescreve
            // a linha errada.
            item_id: item.id,
            product_id: item.product_id,
            production_lot_id: pendingLotChanges[item.id] || item.production_lot_id || null, // Usar lote selecionado no collapse, senão manter o anterior
            expedited_quantity: item.expedited_quantity, // Peso em kg
            box_type: pendingBoxTypeChanges[item.id] || item.box_type || null, // Usar tipo de caixa selecionado, senão manter o anterior
          })
        ),
      };

      // Log detalhado do que está sendo enviado
      console.log("📤 Frontend - Dados sendo enviados para atualização de expedição:");
      console.log(JSON.stringify(dataToSave, null, 2));

      await expeditionService.updateExpedition(expeditionId, dataToSave);

      // Calcular os itens já atualizados com os lotes/caixas recém-guardados
      // (usado tanto para `expeditions` como para `editableExpeditionData` —
      // ver nota abaixo sobre porque as duas precisam de ficar em sincronia).
      const mergeSavedChanges = (items) => items.map(item => {
        const newBoxType = pendingBoxTypeChanges[item.id];
        const newLotId = pendingLotChanges[item.id];

        // Determinar o ID correto para buscar lotes (considerar variantes)
        let lotsProductId = item.product_id;
        if (item.products && item.products.is_variant && item.products.parent_product_id) {
          lotsProductId = item.products.parent_product_id;
        }

        const selectedLot = newLotId ? availableLots[lotsProductId]?.find(lot => lot.id === newLotId) : null;

        return {
          ...item,
          box_type: newBoxType || item.box_type,
          production_lot_id: newLotId || item.production_lot_id,
          production_lots: selectedLot ? {
            lot_number: selectedLot.lot_number,
            production_date: selectedLot.production_date,
            status: selectedLot.status
          } : item.production_lots
        };
      });

      // Atualizar estado local sem recarregar a página
      setExpeditions(prev => prev.map(expedition => {
        if (expedition.id === expeditionId) {
          return {
            ...expedition,
            ...dataToSave,
            expedition_items: mergeSavedChanges(expedition.expedition_items)
          };
        }
        return expedition;
      }));

      // ✅ CRÍTICO: também atualizar editableExpeditionData com os lotes/caixas
      // recém-guardados. Sem isto, um "Guardar" seguinte (ex.: ao mudar o
      // status para "Entregue") volta a usar o snapshot antigo — como
      // pendingLotChanges já foi limpo abaixo, cada item cai de volta no
      // production_lot_id anterior (o mesmo para todos, se vieram de uma
      // divisão), desfazendo silenciosamente as escolhas de lote já gravadas.
      setEditableExpeditionData(prev => ({
        ...prev,
        ...dataToSave,
        expedition_items: mergeSavedChanges(prev.expedition_items || [])
      }));

      // Limpar mudanças pendentes
      setPendingBoxTypeChanges({});
      setPendingLotChanges({});

      // Manter o collapse aberto após o salvamento
      setOpenExpeditionId(currentOpenExpeditionId);

      toast.success("Detalhes da expedição atualizados com sucesso!");
    } catch (err) {
      // Apenas mostrar o toast de erro, sem recarregar a página
      toast.error(`Erro ao guardar detalhes: ${err.message}`);
      // Manter o collapse aberto e os dados editáveis para o usuário poder corrigir
      setOpenExpeditionId(expeditionId);
      // Não atualizar o estado de erro global para não fazer as expedições desaparecerem
    } finally {
      setLoading(false);
    }
  };

  // ⚠️ APENAS EM DESENVOLVIMENTO: Deletar expedição
  const handleDeleteExpedition = async (expeditionId) => {
    if (process.env.NODE_ENV !== 'development') {
      toast.error("Esta funcionalidade só está disponível em ambiente de desenvolvimento");
      return;
    }

    const expedition = expeditions.find(exp => exp.id === expeditionId);
    if (window.confirm(`Tem a certeza que deseja deletar a expedição ${expedition?.order_number || expeditionId}? Esta ação não pode ser desfeita.`)) {
      try {
        setLoading(true);
        await expeditionService.deleteExpedition(expeditionId);
        // Remover a expedição da lista local
        setExpeditions(prev => prev.filter(exp => exp.id !== expeditionId));
        // Fechar o collapse
        setOpenExpeditionId("");
        toast.success("Expedição deletada com sucesso!");
      } catch (err) {
        toast.error(`Erro ao deletar expedição: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  // Funções para dividir expedição
  const handleSplitItemClick = (expeditionId, itemId) => {
    setSplitError("");
    setSplitExpeditionId(expeditionId);
    setSplitItemId(itemId);
    setSplitQuantity(0);
    setShowSplitModal(true);
  };

  const handleCloseSplitModal = () => {
    setShowSplitModal(false);
    setSplitExpeditionId(null);
    setSplitItemId(null);
    setSplitQuantity(0);
    setSplitError("");
  };

  const isSplitByKg = (item) => item?.products?.sold_by === 'kg';

  const handleQuantityChange = (value) => {
    const currentExpedition = expeditions.find(exp => exp.id === splitExpeditionId);
    const currentItem = currentExpedition.expedition_items.find(
      (item) => item.id === splitItemId
    );
    if (!currentItem) return;

    if (isSplitByKg(currentItem)) {
      const maxKg = currentItem.expedited_quantity || 0;
      const parsed = parseFloat(value) || 0;
      const clamped = Math.max(0, Math.min(parsed, maxKg - 0.001));
      setSplitQuantity(Math.round(clamped * 1000) / 1000);
    } else {
      const maxUnits = Math.floor(calculateUnits(currentItem));
      const parsed = parseInt(value, 10) || 0;
      setSplitQuantity(Math.max(0, Math.min(parsed, maxUnits - 1)));
    }
  };

  // Converte splitQuantity para kg conforme o tipo do produto
  const splitQuantityToKg = (item) => {
    if (!item) return 0;
    if (isSplitByKg(item)) return splitQuantity;
    // Por unidade: proporcional ao expedited_quantity
    const totalUnits = calculateUnits(item);
    if (!totalUnits) return 0;
    return Math.round((splitQuantity / totalUnits) * (item.expedited_quantity || 0) * 1000) / 1000;
  };

  const handleConfirmSplit = async () => {
    setSplitError("");

    if (splitQuantity <= 0) {
      setSplitError("Deve especificar uma quantidade válida.");
      return;
    }

    const currentExpedition = expeditions.find(exp => exp.id === splitExpeditionId);
    const currentItem = currentExpedition.expedition_items.find(
      (item) => item.id === splitItemId
    );

    if (isSplitByKg(currentItem)) {
      if (splitQuantity >= (currentItem?.expedited_quantity || 0)) {
        setSplitError("A quantidade a mover deve ser menor que o total do item.");
        return;
      }
    } else {
      if (splitQuantity >= Math.floor(calculateUnits(currentItem))) {
        setSplitError("O número de unidades a mover deve ser menor que o total.");
        return;
      }
    }

    try {
      const kgToSend = splitQuantityToKg(currentItem);
      await expeditionService.splitExpeditionItem(splitExpeditionId, splitItemId, kgToSend);
      toast.success("Item dividido com sucesso!");

      // Manter o collapse aberto durante a atualização
      const originalOpenExpeditionId = openExpeditionId;

      handleCloseSplitModal();

      // Recarregar apenas essa expedição específica
      const updatedExpedition = await expeditionService.getExpeditionById(splitExpeditionId);

      // ✅ CORRIGIDO: Preservar posição e collapse aberto
      setExpeditions(prev => {
        const updated = prev.map(exp => {
          if (exp.id === splitExpeditionId) {
            return {
              ...updatedExpedition,
              expedition_items: updatedExpedition.expedition_items.map(item => ({
                ...item,
                // Se for variante, buscar lotes do produto pai
                products: item.products
              }))
            };
          }
          return exp;
        });
        return updated;
      });

      // ✅ Restaurar collapse aberto para a expedição
      setOpenExpeditionId(originalOpenExpeditionId);

      // ✅ Atualizar dados editáveis para manter o collapse visível com dados atualizados
      const freshExpedition = await expeditionService.getExpeditionById(splitExpeditionId);
      if (freshExpedition) {
        setEditableExpeditionData({
          ...freshExpedition,
          shipping_company: freshExpedition.shipping_company || "",
          invoice_number: freshExpedition.invoice_number || "",
          notes: freshExpedition.notes || "",
        });

        // ✅ Carregar lotes para os novos itens (após divisão)
        for (const item of freshExpedition.expedition_items) {
          let targetProductId = item.product_id;
          if (item.products?.is_variant && item.products?.parent_product_id) {
            targetProductId = item.products.parent_product_id;
          }

          // Se ainda não carregou lotes para este produto, carregar agora
          if (!availableLots[targetProductId]) {
            try {
              const lots = await expeditionService.getAvailableLots(splitExpeditionId, targetProductId);
              setAvailableLots(prev => ({
                ...prev,
                [targetProductId]: lots
              }));
            } catch (err) {
              console.warn(`Aviso ao carregar lotes para produto ${targetProductId}:`, err);
            }
          }
        }
      }

    } catch (err) {
      setSplitError(err.message || "Erro ao dividir o item.");
      console.error("Erro ao dividir o item:", err);
    }
  };


  const handleBoxTypeChange = (expeditionId, itemId, boxType) => {
    const currentOpenExpeditionId = openExpeditionId; // Salvar estado atual

    // Armazenar mudança como pendente (não envia imediatamente)
    setPendingBoxTypeChanges(prev => ({
      ...prev,
      [itemId]: boxType
    }));

    // Atualizar visualização local temporariamente
    setExpeditions(prev => prev.map(expedition =>
      expedition.id === expeditionId ? {
        ...expedition,
        expedition_items: expedition.expedition_items.map(item =>
          item.id === itemId ? { ...item, box_type: boxType } : item
        )
      } : expedition
    ));

    // Manter collapse aberto
    setTimeout(() => setOpenExpeditionId(currentOpenExpeditionId), 0);
  };

  const handleLotChange = (expeditionId, itemId, productId, lotId) => {
    const currentOpenExpeditionId = openExpeditionId; // Salvar estado atual

    // ✅ Se for variante, usar o ID do produto pai para buscar lotes
    let targetProductId = productId;
    const currentExpedition = expeditions.find(exp => exp.id === expeditionId);
    if (currentExpedition) {
      const item = currentExpedition.expedition_items.find(i => i.product_id === productId);
      if (item && item.products && item.products.is_variant && item.products.parent_product_id) {
        targetProductId = item.products.parent_product_id;
        console.log(`📦 Variante detectada: usando lotes do produto pai (${targetProductId})`);
      }
    }

    // Armazenar mudança como pendente (não envia imediatamente)
    setPendingLotChanges(prev => ({
      ...prev,
      [itemId]: lotId
    }));

    // ✅ Buscar dados do lote selecionado usando targetProductId (pai se variante)
    const selectedLot = availableLots[targetProductId]?.find(lot => lot.id === lotId);
    console.log(`🔍 Procurando lote ${lotId} em availableLots[${targetProductId}]`, selectedLot);

    if (selectedLot) {
      console.log(`✅ Lote encontrado: ${selectedLot.lot_number} (${(selectedLot.available_quantity || 0).toFixed(1)} kg disponível)`);
    } else {
      console.warn(`⚠️ Lote ${lotId} não encontrado em availableLots[${targetProductId}]`);
    }

    // Atualizar visualização local temporariamente
    setExpeditions(prev => prev.map(expedition =>
      expedition.id === expeditionId ? {
        ...expedition,
        expedition_items: expedition.expedition_items.map(item =>
          item.id === itemId ? {
            ...item,
            production_lot_id: lotId,
            production_lots: selectedLot ? {
              lot_number: selectedLot.lot_number,
              production_date: selectedLot.production_date,
              status: selectedLot.status
            } : null
          } : item
        )
      } : expedition
    ));

    // Manter collapse aberto
    setTimeout(() => setOpenExpeditionId(currentOpenExpeditionId), 0);
  };

  const expeditionStatusColors = {
    pending: "bg-gray-100 text-gray-800",
    delivered: "bg-green-100 text-green-800",
    cancelled: "bg-red-100 text-red-800",
    // Removidos: packed, overdue, processing, divided, confirmed, on_hold, returned, completed, shipped
  };

  const getOriginLabel = (originSource) => {
    if (originSource === "manual") return "Manual";
    if (originSource === "api") return "API";
    return "Não informado";
  };

  const getOriginClasses = (originSource) => {
    if (originSource === "manual") return "bg-amber-100 text-amber-800";
    if (originSource === "api") return "bg-blue-100 text-blue-800";
    return "bg-neutral-100 text-neutral-700";
  };

  const calculateUnits = (item) => {
    if (
      !item.products ||
      item.expedited_quantity === undefined ||
      item.expedited_quantity === null
    )
      return 0;
    const productWeightPerUnit = getUnitWeight(item.products);

    if (
      productWeightPerUnit === undefined ||
      productWeightPerUnit === null ||
      productWeightPerUnit === 0
    )
      return 0;

    // Unidades individuais = peso total / peso por unidade individual.
    // O multiplicador de pack já está embutido no peso total (expedited_quantity),
    // por isso NÃO se aplica outra vez aqui — fazê-lo duplicava a contagem
    // (ex: 4 packs x6 apareciam como 144 em vez de 24).
    return item.expedited_quantity / productWeightPerUnit;
  };

  // Versão para exibição (fora da divisão): nunca mostra 0 unidades para um
  // item que existe — se o peso por unidade for desconhecido ou o item pesar
  // menos que uma unidade, mostra sempre pelo menos 1.
  const calculateDisplayUnits = (item) => {
    if (!item.products || item.expedited_quantity === undefined || item.expedited_quantity === null) {
      return 0;
    }
    return Math.max(1, calculateUnits(item));
  };

  // Função para calcular o total de unidades
  const calculateTotalUnits = (items) => {
    return items.reduce((total, item) => total + calculateDisplayUnits(item), 0);
  };

  // Função para calcular o peso total
  const calculateTotalWeight = (items) => {
    // expedited_quantity já É o peso total do item — usar diretamente. Não
    // recalcular a partir de unidades × peso unitário: essa conta depende de
    // dados de peso por unidade que só interessam à divisão, e ficava a 0
    // para produtos sem peso unitário definido (ex: veludos), apagando o
    // peso real já guardado.
    return items.reduce((total, item) => total + (item.expedited_quantity || 0), 0);
  };

  // Função auxiliar para formatar valores
  const formatNumber = (value, decimals = 2) => {
    if (value === undefined || value === null) return "0";
    return Number(value).toFixed(decimals);
  };

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-neutral-800">Expedições</h1>
      </div>

      <div className="bg-white shadow-md rounded-lg p-4 sm:p-6 mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Linha 1 */}
          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Status
            </label>
            <select
              name="status"
              value={draftFilters.status}
              onChange={handleDraftChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            >
              <option value="">Todos</option>
              {Object.entries(statusMap).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Encomendas Semanais
            </label>
            <select
              name="is_weekly"
              value={draftFilters.is_weekly}
              onChange={handleDraftChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            >
              <option value="">Todas</option>
              <option value="true">Semanais</option>
              <option value="false">Não Semanais</option>
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Ordenação
            </label>
            <select
              name="sort_order"
              value={draftFilters.sort_order}
              onChange={handleDraftChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            >
              <option value="delivery_asc">Data Entrega ↑ (mais antigas primeiro)</option>
              <option value="delivery_desc">Data Entrega ↓ (mais recentes primeiro)</option>
              <option value="order_date_asc">Data Encomenda ↑ (mais antigas primeiro)</option>
              <option value="order_date_desc">Data Encomenda ↓ (mais recentes primeiro)</option>
              <option value="order_number_desc">Expedição feita mais recentemente</option>
              <option value="order_number_asc">Expedição feita há mais tempo</option>
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Número da Encomenda
            </label>
            <input
              type="text"
              name="order_number"
              value={draftFilters.order_number}
              onChange={handleDraftChange}
              placeholder="Ex: ENC-001"
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            />
          </div>

          {/* Linha 2 */}
          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Nome do Cliente
            </label>
            <input
              type="text"
              name="client_name"
              value={draftFilters.client_name}
              onChange={handleDraftChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Filtrar período por
            </label>
            <select
              name="date_type"
              value={draftFilters.date_type}
              onChange={handleDraftChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            >
              <option value="delivery_date">Data de Entrega</option>
              <option value="order_date">Data de Encomenda</option>
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Período — Início ({draftFilters.date_type === 'order_date' ? 'Encomenda' : 'Entrega'})
            </label>
            <input
              type="date"
              name="start_date"
              value={draftFilters.start_date}
              onChange={handleDraftChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Período — Fim ({draftFilters.date_type === 'order_date' ? 'Encomenda' : 'Entrega'})
            </label>
            <input
              type="date"
              name="end_date"
              value={draftFilters.end_date}
              onChange={handleDraftChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            />
          </div>

          <div className="flex items-end col-span-1 sm:col-span-2 lg:col-span-2 gap-2">
            <button
              onClick={handleSearch}
              className="flex-1 bg-neutral-900 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-neutral-800 text-xs sm:text-sm"
            >
              Buscar
            </button>
            <button
              onClick={handleClearFilters}
              className="flex-1 border border-neutral-300 text-neutral-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-neutral-50 text-xs sm:text-sm"
            >
              Limpar
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <p>Carregando expedições...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {paginatedExpeditions.map((expedition) => (
            <div
              key={expedition.id}
              className={`bg-white shadow-md rounded-lg overflow-hidden ${expedition.orders?.is_weekly ? 'border-2 border-orange-400' : ''}`}
            >
              {/* Linha Principal do Card */}
              <div
                className="p-4 sm:p-6 cursor-pointer flex flex-col sm:flex-row justify-between items-start sm:items-center"
                onClick={() => handleToggleCollapse(expedition.id)}
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-10 gap-2 sm:gap-4 flex-grow w-full">
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-neutral-500">
                      Encomenda
                    </span>
                    <span className="font-semibold text-neutral-800 text-xs sm:text-sm flex items-center gap-1 flex-wrap">
                      {expedition.orders?.order_number || "N/A"}
                      {expedition.orders?.is_weekly && (
                        <span className="inline-block bg-orange-100 text-orange-700 text-xs font-semibold px-1.5 py-0.5 rounded">
                          Semanal
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-neutral-500">
                      Cliente
                    </span>
                    <span className="text-neutral-800 text-xs sm:text-sm">
                      {expedition.orders?.client_name || "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-neutral-500">
                      NIF
                    </span>
                    <span className="text-neutral-800 text-xs sm:text-sm">
                      {expedition.orders?.client_nif || "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-neutral-500">
                      Transporte
                    </span>
                    <span className="text-neutral-800 text-xs sm:text-sm">
                      {expedition.shipping_company || "Não informado"}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-neutral-500">
                      Total Queijos
                    </span>
                    <span className="text-neutral-800 text-xs sm:text-sm">
                      {Math.round(calculateTotalUnits(expedition.expedition_items))}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-neutral-500">
                      Peso Total (kg)
                    </span>
                    <span className="text-neutral-800 text-xs sm:text-sm">
                      {calculateTotalWeight(
                        expedition.expedition_items
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-neutral-500">
                      Encomenda
                    </span>
                    <span className="text-neutral-800 text-xs sm:text-sm">
                      {expedition.orders?.order_date
                        ? formatDateForDisplay(expedition.orders.order_date)
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-neutral-500">
                      Entrega
                    </span>
                    <span className="text-neutral-800 text-xs sm:text-sm">
                      {expedition.orders?.delivery_date
                        ? formatDateForDisplay(expedition.orders.delivery_date)
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-neutral-500">
                      Status
                    </span>
                    <span
                      className={`px-1 py-0.5 sm:px-2 sm:py-1 text-xs rounded-full ${
                        expeditionStatusColors[expedition.status] ||
                        "bg-neutral-100 text-neutral-800"
                      }`}
                    >
                      {statusMap[expedition.status] || expedition.status}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-neutral-500">
                      Origem
                    </span>
                    <span className={`px-1 py-0.5 sm:px-2 sm:py-1 text-xs rounded-full w-fit ${getOriginClasses(expedition.origin_source)}`}>
                      {getOriginLabel(expedition.origin_source)}
                    </span>
                  </div>
                  <div className="flex flex-col items-center justify-center sm:col-span-2 lg:col-span-1">
                    <span className="text-xs font-medium text-neutral-500">
                      Embalado
                    </span>
                    {expedition.status === 'delivered' || expedition.is_packed ? (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-green-500 text-lg sm:text-xl"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faTimesCircle}
                        className="text-red-500 text-lg sm:text-xl"
                      />
                    )}
                  </div>
                </div>
                <FontAwesomeIcon
                  icon={
                    openExpeditionId === expedition.id
                      ? faChevronUp
                      : faChevronDown
                  }
                  className="text-neutral-600 mt-2 sm:mt-0 sm:ml-4 text-sm sm:text-base"
                />
              </div>

              {/* Conteúdo do Collapse - Removido overflow-hidden que causava corte */}
              <div
                className={`transition-all duration-300 ease-in-out ${
                  openExpeditionId === expedition.id
                    ? "max-h-none opacity-100"
                    : "max-h-0 opacity-0 overflow-hidden"
                }`}
              >
                <div className="p-3 sm:p-6 border-t border-neutral-200 bg-neutral-50">
                  <h3 className="text-lg font-semibold text-neutral-800 mb-4">
                    Detalhes da Expedição
                  </h3>

                  {/* Informações do Destinatário */}
                  <div className="mb-4 sm:mb-6">
                    <h4 className="text-sm sm:text-md font-medium text-neutral-700 mb-2">
                      Destinatário
                    </h4>
                    <div className="text-xs sm:text-sm space-y-1">
                      <p className="text-neutral-600">
                        <strong>Endereço:</strong>{" "}
                        {expedition.orders?.destination_address || "N/A"}
                      </p>
                      <p className="text-neutral-600">
                        <strong>Recebedor:</strong>{" "}
                        {expedition.orders?.recipient_name || "N/A"}
                      </p>
                      <p className="text-neutral-600">
                        <strong>Origem:</strong>{" "}
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getOriginClasses(expedition.origin_source)}`}>
                          {getOriginLabel(expedition.origin_source)}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Detalhes da Embalagem (Campos Editáveis) */}
                  <div className="mb-4 sm:mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-neutral-700 mb-1">
                        Status da Expedição
                      </label>
                      <select
                        name="status"
                        value={editableExpeditionData.status || ""}
                        onChange={handleEditableChange}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
                      >
                        {Object.entries(statusMap).map(([key, value]) => (
                          <option key={key} value={key}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-neutral-700 mb-1">
                        Transportadora
                      </label>
                      <input
                        type="text"
                        name="shipping_company"
                        value={editableExpeditionData.shipping_company || ""}
                        onChange={handleEditableChange}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-neutral-700 mb-1">
                        Número da Fatura
                      </label>
                      <input
                        type="text"
                        name="invoice_number"
                        value={editableExpeditionData.invoice_number || ""}
                        onChange={handleEditableChange}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
                      />
                    </div>
                    {/* Campos de Tipo de Embalagem e Caixa de Transporte removidos */}
                    <div className="col-span-full">
                      <label className="block text-xs sm:text-sm font-medium text-neutral-700 mb-1">
                        Observações
                      </label>
                      <textarea
                        name="notes"
                        value={editableExpeditionData.notes || ""}
                        onChange={handleEditableChange}
                        rows="3"
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
                      ></textarea>
                    </div>
                    <div className="col-span-full mb-3 sm:mb-4">
                      <button
                        onClick={() => handleSaveDetails(expedition.id)}
                        className="w-full sm:w-auto bg-neutral-900 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-neutral-800 text-xs sm:text-sm"
                        disabled={loading}
                      >
                        {loading ? "Guardando..." : "Guardar Alterações"}
                      </button>
                    </div>
                  </div>

                  {/* Produtos da Expedição */}
                  <div className="mb-4 sm:mb-6">
                    <h4 className="text-sm sm:text-md font-medium text-neutral-700 mb-2">
                      Produtos da Expedição
                    </h4>
                    {expedition.expedition_items &&
                    expedition.expedition_items.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full border border-neutral-200 rounded-lg">
                          <thead className="bg-neutral-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                Produto
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                Código
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                Lote
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                Unidades
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                Peso (kg)
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                Tipo Caixa
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-neutral-600">
                                Ações
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-neutral-100">
                            {expedition.expedition_items.map((item) => (
                              <tr key={item.id}>
                                <td className="px-3 py-2 text-xs">{item.products?.name || "Produto Desconhecido"}</td>
                                <td className="px-3 py-2 text-xs">
                                  {item.products?.product_code || "N/A"}
                                </td>
                                <td className="px-3 py-2 text-xs">
                                  <div className="space-y-1">
                                    {(() => {
                                      // Se for variante, buscar lotes do produto pai
                                      let targetProductId = item.product_id;
                                      if (item.products && item.products.is_variant && item.products.parent_product_id) {
                                        targetProductId = item.products.parent_product_id;
                                      }
                                      // Lote padrão: usar pendingLotChanges > production_lot_id > primeiro da lista (maior estoque)
                                      const lots = availableLots[targetProductId] || [];
                                      const selectedLotId = pendingLotChanges[item.id] || item.production_lot_id || (lots.length > 0 ? lots[0].id : '');
                                      return (
                                        <>
                                          {(() => {
                                            return (
                                              <select
                                                className="border rounded px-1 py-0.5 text-xs w-full max-w-24"
                                                value={selectedLotId}
                                                onChange={(e) => handleLotChange(expedition.id, item.id, item.product_id, e.target.value)}
                                              >
                                                <option value="">Selecionar</option>
                                                {lots.map(lot => (
                                                  <option key={lot.id} value={lot.id}>
                                                    {lot.lot_number} ({(lot.available_quantity || 0).toFixed(1)} kg disponível)
                                                  </option>
                                                ))}
                                              </select>
                                            );
                                          })()}
                                          {item.production_lots && (
                                            <div className="text-xs text-neutral-500">
                                              {item.production_lots.lot_number}
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-xs">
                                  {calculateDisplayUnits(item).toFixed(0)}
                                </td>
                                <td className="px-3 py-2 text-xs">
                                  {(item.expedited_quantity || 0).toFixed(2)}
                                </td>
                                <td className="px-3 py-2 text-xs">
                                  <select
                                    className="border rounded px-1 py-0.5 text-xs w-full max-w-20"
                                    value={item.box_type || ''}
                                    onChange={(e) => handleBoxTypeChange(expedition.id, item.id, e.target.value)}
                                  >
                                    <option value="">Selecionar tipo de caixa</option>
                                    {(item.box_type && !boxTypeOptions.includes(item.box_type)
                                      ? [item.box_type, ...boxTypeOptions]
                                      : boxTypeOptions
                                    ).map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-3 py-2 text-xs">
                                  <button
                                    onClick={() => handleSplitItemClick(expedition.id, item.id)}
                                    className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                    title="Dividir este item"
                                  >
                                    Dividir
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-neutral-500 text-xs sm:text-sm">
                        Nenhum produto nesta expedição.
                      </p>
                    )}
                  </div>

                  {/* Ações - Seção final com espaçamento adequado */}
                  <div className="pt-3 border-t border-neutral-200">
                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
                      {/* ⚠️ APENAS EM DESENVOLVIMENTO: Botão deletar */}
                      {process.env.NODE_ENV === 'development' && (
                        <button
                          onClick={() => handleDeleteExpedition(expedition.id)}
                          className="w-full sm:w-auto bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-xs sm:text-sm font-medium"
                        >
                          🗑️ Deletar Expedição
                        </button>
                      )}
                      <button
                        onClick={() =>
                          navigate(`/edit-expedition/${expedition.id}`)
                        }
                        className="w-full sm:w-auto bg-neutral-900 text-white px-4 py-2 rounded-md hover:bg-neutral-800 text-xs sm:text-sm font-medium"
                      >
                        Editar Expedição
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {paginatedExpeditions.length === 0 && (
            <div className="text-center py-12">
              <div className="text-neutral-500 mb-6">
                <p className="text-lg mb-2">Nenhuma expedição encontrada</p>
                <p className="text-sm">Verifique os filtros ou crie uma nova expedição</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-8 gap-4">
            <div className="text-sm text-neutral-600">
              Mostrando {startIndex + 1} a {Math.min(endIndex, totalItems)} de {totalItems} expedições
            </div>
            <nav className="flex items-center space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>

              {[...Array(Math.min(3, totalPages))].map((_, index) => {
                let pageNumber;
                if (totalPages <= 3) {
                  pageNumber = index + 1;
                } else if (currentPage <= 2) {
                  pageNumber = index + 1;
                } else if (currentPage >= totalPages - 1) {
                  pageNumber = totalPages - 2 + index;
                } else {
                  pageNumber = currentPage - 1 + index;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`px-3 py-2 text-sm rounded-lg ${
                      currentPage === pageNumber
                        ? 'bg-neutral-900 text-white'
                        : 'border border-neutral-200 hover:bg-neutral-50'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Seguinte
              </button>
            </nav>
          </div>
        )}

      {/* Modal/Popup para Dividir Item da Expedição */}
      {showSplitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Dividir Item da Expedição</h3>
              <button
                onClick={handleCloseSplitModal}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {splitError && (
              <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4">
                {splitError}
              </div>
            )}

            {splitItemId && (() => {
              const currentExpedition = expeditions.find(exp => exp.id === splitExpeditionId);
              const currentItem = currentExpedition?.expedition_items.find(
                (item) => item.id === splitItemId
              );
              if (!currentItem) return null;

              const byKg = isSplitByKg(currentItem);
              const totalKg = currentItem.expedited_quantity || 0;
              const totalUnits = Math.floor(calculateUnits(currentItem));
              const kgPreview = splitQuantityToKg(currentItem);
              const remainingKg = Math.max(0, totalKg - kgPreview);
              const remainingUnits = byKg ? null : Math.max(0, totalUnits - (splitQuantity || 0));

              return (
                <div className="space-y-4">
                  <div className="bg-neutral-50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Item Selecionado:</h4>
                    <p className="text-sm text-neutral-600">{currentItem.products.name}</p>
                    <p className="text-sm text-neutral-500">
                      Total: {totalKg.toFixed(3)} kg
                      {!byKg && ` (${totalUnits} un)`}
                    </p>
                    <p className="text-xs text-neutral-400 mt-1">
                      Modo: {byKg ? 'por peso (kg)' : 'por unidade'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-neutral-700">
                      {byKg ? 'Quantos kg mover para nova expedição?' : 'Quantas unidades mover para nova expedição?'}
                    </label>
                    <input
                      type="number"
                      value={splitQuantity}
                      onChange={(e) => handleQuantityChange(e.target.value)}
                      className="w-full p-3 border rounded-lg"
                      min={byKg ? "0.001" : "1"}
                      max={byKg ? totalKg - 0.001 : totalUnits - 1}
                      step={byKg ? "0.001" : "1"}
                      placeholder={byKg ? "Ex: 0.400" : "Ex: 2"}
                    />
                    <div className="grid grid-cols-2 gap-2 text-xs text-neutral-500">
                      <p>
                        Mover:{" "}
                        <span className="font-semibold text-neutral-700">
                          {byKg ? `${(splitQuantity || 0).toFixed(3)} kg` : `${splitQuantity || 0} un (${kgPreview.toFixed(3)} kg)`}
                        </span>
                      </p>
                      <p>
                        Fica:{" "}
                        <span className="font-semibold text-neutral-700">
                          {byKg ? `${remainingKg.toFixed(3)} kg` : `${remainingUnits} un (${remainingKg.toFixed(3)} kg)`}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={handleCloseSplitModal}
                className="px-4 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmSplit}
                className="px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800"
              >
                Dividir Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expediction;
